import React, { useState } from 'react';
import { Search, ShieldCheck, Loader2, X, TrendingUp, MapPin, AlertTriangle, Plus, Check } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { apiService } from '../services/api';

export const BrandSearch = ({ onEntityClick, selectedGeo }) => {
  const PAGE_SIZE = 100;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [checking, setChecking] = useState(false);
  const [checkStatus, setCheckStatus] = useState(null);
  const [addedIds, setAddedIds] = useState(new Set());
  const [verificationFilter, setVerificationFilter] = useState('all');

  const runSearch = async ({ append = false } = {}) => {
    const q = query.trim();
    if (q && q.length < 2) return;
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setSearching(true);
        setResults(null);
        setCheckStatus(null);
      }
      const skip = append ? (results?.results?.length || 0) : 0;
      const res = await apiService.searchEntities(q, selectedGeo, PAGE_SIZE, skip, verificationFilter);
      const payload = res.data || {};
      const incoming = payload.results || [];
      const nextResults = append ? [...(results?.results || []), ...incoming] : incoming;

      setResults({
        ...payload,
        results: nextResults,
        count: nextResults.length,
      });
      setTotal(Number(payload.total || 0));
      setHasMore(Boolean(payload.has_more));
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setSearching(false);
      }
    }
  };

  const handleSearch = async () => {
    await runSearch({ append: false });
  };

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) return;
    await runSearch({ append: true });
  };

  const handleCheck = async () => {
    if (!query.trim() || query.trim().length < 2) return;
    try {
      setChecking(true);
      setCheckStatus(null);
      const res = await apiService.checkBrand(query.trim());
      setCheckStatus(res.data);
    } catch (err) {
      console.error('Check error:', err);
    } finally {
      setChecking(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleClear = () => {
    setQuery('');
    setResults(null);
    setCheckStatus(null);
  };

  const handleAddToWatchlist = async (e, entityId) => {
    e.stopPropagation();
    try {
      await apiService.addToWatchlist(entityId);
      setAddedIds(prev => new Set([...prev, entityId]));
    } catch (err) {
      console.error('Add to watchlist error:', err);
    }
  };

  return (
    <Card className="card-elevated mb-6">
      <CardContent className="p-4">
        <div className="flex gap-2 items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search brand or leave empty to browse list"
              className="w-full pl-10 pr-8 py-2 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {query && (
              <button onClick={handleClear} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <select
            value={verificationFilter}
            onChange={(e) => setVerificationFilter(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-md text-sm text-foreground"
            title="Verification filter for list/search"
          >
            <option value="all">All statuses</option>
            <option value="verified">Verified only</option>
            <option value="confirmed_or_likely">Verified + likely</option>
            <option value="likely">Likely only</option>
            <option value="unverified">Unverified</option>
            <option value="not_gambling">Not gambling</option>
          </select>
          <button
            onClick={handleSearch}
            disabled={searching}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Find
          </button>
          <button
            onClick={handleCheck}
            disabled={checking || !query.trim()}
            className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
            Check Brand
          </button>
        </div>

        {/* Check status banner */}
        {checkStatus && (
          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-300 rounded-md">
            <p className="text-sm text-emerald-700 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              <span><strong>{checkStatus.brand}</strong> — checking across {checkStatus.geos?.length || 0} GEOs. Results in ~1-3 min.</span>
            </p>
            <p className="text-xs text-muted-foreground mt-1">Track progress in Settings → Pipeline Status. Click "Find" after to see results.</p>
          </div>
        )}

        {/* Search results */}
        {results && (
          <div className="mt-3">
            {results.count === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-yellow-700" />
                <p className="text-sm">No results{results.query ? <> for "<strong>{results.query}</strong>"</> : ''}</p>
                <p className="text-xs mt-1">Try "Check Brand" to collect data for this brand</p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Showing {results.count} of {total} result{total > 1 ? 's' : ''}{results.query ? <> for "{results.query}"</> : ''}{selectedGeo ? ` in ${selectedGeo}` : ' across all GEOs'}
                </p>
                {results.results.map((entity) => (
                  <div
                    key={entity.id}
                    onClick={() => onEntityClick(entity.id)}
                    className="flex items-center justify-between p-3 bg-background/50 border border-border rounded-md cursor-pointer hover:border-primary/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{entity.canonical_name}</span>
                        {entity.official_name && (
                          <span className="text-xs text-blue-700">({entity.official_name})</span>
                        )}
                        <Badge variant="outline" className="text-xs">{entity.niche}</Badge>
                        {entity.entity_type === 'online_casino' && entity.verification_status === 'verified_casino' && (
                          <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700">Casino </Badge>
                        )}
                        {entity.entity_type === 'online_casino' && entity.verification_status === 'likely_casino' && (
                          <Badge variant="outline" className="text-xs border-yellow-300 text-yellow-700">Casino ?</Badge>
                        )}
                        {entity.entity_type === 'online_casino' && (!entity.verification_status || entity.verification_status === 'unverified') && (
                          <Badge variant="outline" className="text-xs border-gray-300 text-gray-700">Casino ...</Badge>
                        )}
                        {entity.entity_type === 'not_gambling' && (
                          <Badge variant="outline" className="text-xs border-red-300 text-red-700">Not Gambling</Badge>
                        )}
                        {entity.entity_type === 'offline_casino' && (
                          <Badge variant="outline" className="text-xs border-orange-300 text-orange-700">Offline</Badge>
                        )}
                        {entity.entity_type === 'sportsbook' && (
                          <Badge variant="outline" className="text-xs border-blue-300 text-blue-700">Sportsbook</Badge>
                        )}
                        {(!entity.entity_type || entity.entity_type === 'unknown') && (
                          <Badge variant="outline" className="text-xs bg-yellow-50 text-yellow-700 border-yellow-300">?</Badge>
                        )}
                        {entity.spike_count_7d > 0 && (
                          <Badge variant="default" className="text-xs bg-accent/20 text-accent">
                            {entity.spike_count_7d} spike{entity.spike_count_7d > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {entity.geo_scores.map(g => g.geo).join(', ') || 'No GEO data'}
                        </span>
                        {entity.first_seen_geo && (
                          <span>First seen: {entity.first_seen_geo}</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right ml-4 flex items-center gap-3">
                      {entity.pulse > 0 && (
                        <div className="text-center">
                          <span className={`text-sm font-bold ${
                            entity.pulse >= 80 ? 'text-red-700' :
                            entity.pulse >= 60 ? 'text-emerald-700' :
                            entity.pulse >= 40 ? 'text-yellow-700' :
                            entity.pulse >= 20 ? 'text-blue-700' : 'text-muted-foreground'
                          }`}>{entity.pulse}</span>
                          <p className="text-[9px] text-muted-foreground">Pulse</p>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-1 text-sm font-medium text-emerald-700">
                          <TrendingUp className="w-4 h-4" />
                          {entity.total_score_7d}
                        </div>
                        <p className="text-xs text-muted-foreground">14d score</p>
                      </div>
                      <button
                        onClick={(e) => handleAddToWatchlist(e, entity.id)}
                        disabled={addedIds.has(entity.id)}
                        className={`p-1.5 rounded-md border transition-colors ${
                          addedIds.has(entity.id)
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-700 cursor-default'
                            : 'border-border text-muted-foreground hover:text-foreground hover:border-primary/50'
                        }`}
                        title={addedIds.has(entity.id) ? 'Added to Cabinet' : 'Add to Cabinet'}
                      >
                        {addedIds.has(entity.id) ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                ))}
                {hasMore && (
                  <div className="pt-2">
                    <button
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="px-4 py-2 bg-background border border-border text-foreground rounded-md hover:border-primary/50 disabled:opacity-50 flex items-center gap-2"
                    >
                      {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {loadingMore ? 'Loading...' : `Load more (${Math.max(total - results.count, 0)} left)`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
